import type { Href } from "expo-router";
import { useGlobalSearchParams, usePathname, useRouter } from "expo-router";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  Modal as RNModal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  type ViewStyle,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { signOutToHome } from "../../lib/auth";
import { useAuth } from "../../lib/authContext";
import { COLORS, FONTS, RADII, SPACING, STROKES } from "../../lib/brand";
import { LogoMark, LogoWordmark } from "../brand/Logo";
import { IconAuditLog, IconCalendar, IconCampus, IconChart, IconClients, IconClipboardCheck, IconCompetitions, IconDashboard, IconDatabase, IconDollar, IconEnvelope, IconGavel, IconGlobe, IconInquiries, IconMegaphone, IconPlus, IconSearch, IconShield, IconStudents, IconTeam, IconUsers } from "./AdminIcons";
import { AdminGlobalSearchOverlay } from "./AdminGlobalSearchOverlay";
import { PANE_THEMES } from "../brand/paneThemes";
import { countPendingApprovals } from "../../lib/caseApprovals";
import { getInquiriesStats } from "../../lib/inquiries";
import { listInvites } from "../../lib/invites";
import { useShellResponsive } from "../../lib/useShellResponsive";
import { HamburgerButton, ShellMobileDrawer } from "../ShellMobileDrawer";

/**
 * Admin chrome — single dark sidebar mounted once at the (admin) layout
 * level. Mirrors the pattern OverviewShell uses on (client) and JudgeShell
 * uses on (judge): hoisted into `_layout.tsx` so navigation between admin
 * pages doesn't unmount the shell or flash the nav.
 *
 * The shell is "dumb" — it self-resolves identity (full_name + role) from
 * useAuth, derives the active nav key from the pathname, and never fetches
 * its own data.
 */

// Admin chrome is locked to the Admin pane theme so editing /brand (Admin)
// propagates here. Local const names kept so the JSX below is untouched —
// only the source of each value changed.
const THEME = PANE_THEMES.Admin;
const BLACK = THEME.pageBg;
const FOREST = THEME.sidebarBg;
const FOREST_MID = THEME.surfaceRaisedBg;
const AMBER = THEME.accent;
const LINEN = THEME.textOnSurface;
const SAGE = COLORS.SAGE.hex;

const SIDEBAR_W = 240;
const TOPBAR_H = 52;

const W = "rgba(255,255,255,1)";
const BORDER = "rgba(255,255,255,0.07)";
const SECTION_LABEL = "rgba(237,232,223,0.30)";

type AdminNavKey =
  | "dashboard"
  | "inquiries"
  | "case_approvals"
  | "invites"
  | "companies"
  | "students"
  | "judges"
  | "calendar"
  | "competitions"
  | "announcements"
  | "new_rm"
  | "internal_team"
  | "campus_admins"
  | "all_users"
  | "domains"
  | "audit_log"
  | "disbursements"
  | "promos"
  | "analytics"
  | "sql_console";

type NavRow = {
  key: AdminNavKey;
  label: string;
  href: Href;
  icon: (color: string) => ReactNode;
  /** When set, the nav row is rendered with a + glyph + dashed amber outline. */
  isQuickCreate?: boolean;
};

type NavSection = {
  label: string;
  rows: NavRow[];
  /** Config / rarely-touched groups: sunk to the bottom, set off by a divider
   *  and rendered a notch quieter so the daily-driver sections read primary. */
  muted?: boolean;
  /** Only rendered when the signed-in user is a super_admin. */
  superAdminOnly?: boolean;
};

// Ordered by real daily usage: the inbox an admin lives in sits at the top and
// reads primary; people + programs in the middle; rarely-touched config sinks
// to a de-emphasized group at the bottom. Reorder here = reorder the sidebar.
const NAV_SECTIONS: NavSection[] = [
  {
    label: "Dashboard",
    rows: [
      { key: "dashboard", label: "Dashboard", href: "/admin" as Href, icon: (c) => <IconDashboard color={c} /> },
    ],
  },
  {
    label: "Inbox",
    rows: [
      { key: "case_approvals", label: "Case approvals", href: "/admin/case-approvals" as Href, icon: (c) => <IconClipboardCheck color={c} /> },
      { key: "inquiries",      label: "Inquiries",      href: "/admin/inquiries"      as Href, icon: (c) => <IconInquiries color={c} /> },
      { key: "invites",        label: "Invites",        href: "/admin/invites"        as Href, icon: (c) => <IconEnvelope color={c} /> },
    ],
  },
  {
    label: "CRM",
    rows: [
      { key: "companies", label: "Companies", href: "/admin/clients"            as Href, icon: (c) => <IconClients color={c} /> },
      { key: "students",  label: "Students",  href: "/admin/students"           as Href, icon: (c) => <IconStudents color={c} /> },
      { key: "judges",    label: "Judges",    href: "/admin/judges"             as Href, icon: (c) => <IconGavel color={c} /> },
      { key: "calendar",  label: "Calendar",  href: "/admin/calendar"           as Href, icon: (c) => <IconCalendar color={c} /> },
    ],
  },
  {
    label: "Programs",
    rows: [
      { key: "competitions",   label: "Competitions",     href: "/admin/competitions"    as Href, icon: (c) => <IconCompetitions color={c} /> },
      { key: "disbursements",  label: "Disbursements",    href: "/admin/disbursements"   as Href, icon: (c) => <IconDollar color={c} /> },
      { key: "announcements",  label: "Announcements",    href: "/admin/announcements"   as Href, icon: (c) => <IconMegaphone color={c} /> },
      { key: "new_rm",         label: "New RM program",   href: "/admin/programs/new-rm" as Href, icon: (c) => <IconPlus color={c} />, isQuickCreate: true },
    ],
  },
  {
    label: "Config",
    muted: true,
    rows: [
      { key: "internal_team",  label: "Internal team",     href: "/admin/team"          as Href, icon: (c) => <IconTeam color={c} /> },
      { key: "campus_admins",  label: "Campus admins",     href: "/admin/campus-admins" as Href, icon: (c) => <IconCampus color={c} /> },
      { key: "all_users",      label: "All users + roles", href: "/admin/users"         as Href, icon: (c) => <IconUsers color={c} /> },
      { key: "domains",        label: "Email domains",     href: "/admin/domains"       as Href, icon: (c) => <IconGlobe color={c} /> },
      { key: "promos",         label: "Promo codes",       href: "/admin/promos"        as Href, icon: (c) => <IconDollar color={c} /> },
      { key: "audit_log",      label: "Audit log",         href: "/admin/audit-log"     as Href, icon: (c) => <IconAuditLog color={c} /> },
    ],
  },
  {
    label: "Super admin",
    muted: true,
    superAdminOnly: true,
    rows: [
      { key: "analytics",   label: "Analytics",   href: "/admin/analytics"   as Href, icon: (c) => <IconChart color={c} /> },
      { key: "sql_console", label: "SQL console", href: "/admin/sql-console" as Href, icon: (c) => <IconDatabase color={c} /> },
    ],
  },
];

/**
 * Pick the active nav key from the pathname. Detail routes
 * (`/admin/company/[id]`, `/admin/student/[id]`) highlight the list-page
 * nav row they came from. /admin/users with `?role=judge` lights up the
 * "Judges" placeholder; without the query it lights up "All users + roles".
 */
function navKeyFromPath(path: string, queryRole: string | null): AdminNavKey | null {
  const p = (path.split("?")[0] ?? "").replace(/\/+$/, "") || "/";

  if (p === "/admin") return "dashboard";
  if (p === "/admin/inquiries") return "inquiries";
  if (p === "/admin/case-approvals") return "case_approvals";
  if (p === "/admin/invites") return "invites";
  if (p === "/admin/clients") return "companies";
  if (p === "/admin/students") return "students";
  if (p === "/admin/judges") return "judges";
  if (p === "/admin/users") return queryRole === "judge" ? "judges" : "all_users";
  if (p === "/admin/calendar") return "calendar";
  if (p === "/admin/competitions") return "competitions";
  if (p === "/admin/announcements") return "announcements";
  if (p === "/admin/team") return "internal_team";
  if (p === "/admin/campus-admins") return "campus_admins";
  if (p === "/admin/domains") return "domains";
  if (p === "/admin/audit-log") return "audit_log";
  if (p === "/admin/analytics") return "analytics";
  if (p === "/admin/sql-console") return "sql_console";
  if (p === "/admin/disbursements") return "disbursements";
  if (p === "/admin/promos") return "promos";

  // Detail / nested routes.
  if (p === "/admin/programs/new-rm" || p.startsWith("/admin/programs/")) return "new_rm";
  if (p.startsWith("/admin/competitions/")) return "competitions";
  if (p.startsWith("/admin/student/")) return "students";
  if (p.startsWith("/admin/judge/")) return "judges";
  if (p.startsWith("/admin/company/")) return "companies";
  // /admin/account, /admin/security, /admin/mfa/*, /admin/pending,
  // /admin/rejected — none light up the sidebar.
  return null;
}

function topbarTitleFromPath(path: string, queryRole: string | null): string {
  if (path.startsWith("/admin/student/")) return "Student";
  if (path.startsWith("/admin/judge/")) return "Judge";
  if (path.startsWith("/admin/company/")) return "Company";
  if (path === "/admin/account" || path.startsWith("/admin/account/")) return "Account & security";
  if (path === "/admin/security" || path.startsWith("/admin/security/")) return "Account & security";
  if (path.startsWith("/admin/mfa/")) return "Two-factor setup";
  if (path === "/admin/pending") return "Pending approval";
  if (path === "/admin/rejected") return "Application rejected";
  const key = navKeyFromPath(path, queryRole);
  if (!key) return "Admin";
  for (const section of NAV_SECTIONS) {
    const row = section.rows.find((r) => r.key === key);
    if (row) return row.label;
  }
  return "Admin";
}

function makeInitials(name: string | null | undefined, emailFallback: string | null): string {
  const src = (name ?? "").trim() || (emailFallback ?? "").split("@")[0] || "";
  if (!src) return "AD";
  const parts = src.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function roleLineFromRole(role: string | null | undefined): string {
  if (role === "super_admin") return "Super admin";
  if (role === "admin_licensing") return "Licensing admin";
  if (role === "admin_rm") return "RM admin";
  if (role) return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return "Admin";
}

export function AdminShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useGlobalSearchParams<{ role?: string | string[] }>();
  const queryRole = Array.isArray(params.role) ? params.role[0] ?? null : params.role ?? null;
  const { user, role } = useAuth();
  const { isMobile, showSidebar, drawerOpen, openDrawer, closeDrawer } =
    useShellResponsive();

  const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const fullName = typeof meta.full_name === "string" ? meta.full_name.trim() : "";
  const email = user?.email ?? null;
  const displayName = fullName || (email ? email.split("@")[0] : "Admin");
  const initials = makeInitials(fullName, email);
  const roleLine = roleLineFromRole(role);

  const activeKey = navKeyFromPath(pathname, queryRole);
  const topbarTitle = topbarTitleFromPath(pathname, queryRole);

  // ── Inbox badges (inquiries new + cases pending review + pending invites) ──
  const [inquiriesNew, setInquiriesNew] = useState(0);
  const [pendingInvites, setPendingInvites] = useState(0);
  const [casesPending, setCasesPending] = useState(0);

  const refreshBadges = useCallback(async () => {
    try {
      const [iq, inv, casePending] = await Promise.all([
        getInquiriesStats(),
        listInvites({ status: "pending" }),
        countPendingApprovals(),
      ]);
      setInquiriesNew(iq.new_count ?? 0);
      setPendingInvites(inv.length);
      setCasesPending(casePending);
    } catch {
      /* swallow — the shell never blocks navigation on a counts read */
    }
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    void refreshBadges();
  }, [user?.id, pathname, refreshBadges]);

  function badgeFor(key: AdminNavKey): number {
    if (key === "inquiries") return inquiriesNew;
    if (key === "invites") return pendingInvites;
    if (key === "case_approvals") return casesPending;
    return 0;
  }

  // ── Global search palette ──
  const [searchOpen, setSearchOpen] = useState(false);

  // Cmd/Ctrl+K toggles the palette from anywhere; "/" opens it too, but only
  // when the user isn't already typing in a field (so it doesn't hijack a
  // slash mid-search or mid-form). Mounted at shell level so the binding is
  // live on every admin page. Web only.
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
        return;
      }
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const el = (typeof document !== "undefined" ? document.activeElement : null) as
          | HTMLElement
          | null;
        const tag = el?.tagName;
        const typing =
          tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable === true;
        if (!typing) {
          e.preventDefault();
          setSearchOpen(true);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── User menu dropdown ──
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const avatarRef = useRef<View | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ top: number; right: number }>({ top: TOPBAR_H + 4, right: 16 });

  function openAccountMenu() {
    if (Platform.OS === "web" && avatarRef.current) {
      const node = avatarRef.current as unknown as { getBoundingClientRect?: () => DOMRect };
      const rect = node.getBoundingClientRect?.();
      if (rect && typeof window !== "undefined") {
        setMenuAnchor({
          top: Math.round(rect.bottom + 6),
          right: Math.max(8, Math.round(window.innerWidth - rect.right)),
        });
      }
    }
    setAccountMenuOpen(true);
  }

  // ESC to close (web).
  useEffect(() => {
    if (Platform.OS !== "web" || !accountMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAccountMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [accountMenuOpen]);

  /** Closes the drawer (no-op on desktop) before navigating, so a tap on a
   *  nav row in the mobile overlay dismisses the panel and routes in one step. */
  const navTo = useCallback(
    (href: Href) => {
      closeDrawer();
      router.push(href);
    },
    [closeDrawer, router],
  );

  const sidebarBody = (
    <>
      {/* Logo header → /admin */}
      <Pressable
        onPress={() => navTo("/admin" as Href)}
              style={{
                height: TOPBAR_H,
                paddingHorizontal: 18,
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
                borderBottomWidth: STROKES.hairline,
                borderBottomColor: "rgba(181,135,58,0.12)",
              }}
            >
              <LogoMark size={28} tile={FOREST_MID} ringOuter={SAGE} ringInner={AMBER} dot={AMBER} />
              <View style={{ minWidth: 0, flexShrink: 1 }}>
                <LogoWordmark
                  numberOfLines={1}
                  fontSize={17}
                  letterSpacing={3}
                  color={LINEN}
                  accent={AMBER}
                />
                <Text
                  numberOfLines={1}
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 8.5,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    color: "rgba(237,232,223,0.28)",
                    marginTop: 1,
                  }}
                >
                  Admin
                </Text>
              </View>
            </Pressable>

            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ paddingTop: SPACING.sm, paddingBottom: SPACING.sm }}
              showsVerticalScrollIndicator={false}
            >
              {NAV_SECTIONS.filter((s) => !s.superAdminOnly || role === "super_admin").map((section, sectionIdx) => (
                <View key={section.label} style={sectionIdx > 0 ? { marginTop: section.muted ? 18 : 14 } : null}>
                  {/* Muted config group is set off by a hairline rule so the
                      daily-driver sections above read as the primary nav. */}
                  {section.muted ? (
                    <View
                      style={{
                        height: STROKES.hairline,
                        backgroundColor: THEME.surfaceDividerFaint,
                        marginHorizontal: 18,
                        marginBottom: 14,
                      }}
                    />
                  ) : null}
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 8.5,
                      letterSpacing: 2.4,
                      textTransform: "uppercase",
                      color: SECTION_LABEL,
                      paddingHorizontal: 18,
                      marginBottom: 6,
                      opacity: section.muted ? 0.7 : 1,
                    }}
                  >
                    {section.label}
                  </Text>
                  {section.rows.map((row) => {
                    const active = row.key === activeKey;
                    const idleColor = section.muted ? "rgba(237,232,223,0.34)" : "rgba(237,232,223,0.42)";
                    const color = active ? LINEN : idleColor;
                    return (
                      <SidebarNavItem
                        key={row.key}
                        label={row.label}
                        active={active}
                        onPress={() => navTo(row.href)}
                        icon={row.icon(color)}
                        badge={badgeFor(row.key)}
                        isQuickCreate={row.isQuickCreate}
                        muted={section.muted}
                      />
                    );
                  })}
                </View>
              ))}
            </ScrollView>

            {/* Footer (name + role + Account & Security + sign out) */}
            <View
              style={{
                paddingHorizontal: 18,
                paddingVertical: SPACING.sm,
                borderTopWidth: STROKES.hairline,
                borderTopColor: "rgba(181,135,58,0.12)",
              }}
            >
              <Text
                style={{ fontFamily: FONTS.bodySemiBold, fontSize: 12, color: LINEN }}
                numberOfLines={1}
              >
                {displayName}
              </Text>
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 10,
                  color: "rgba(237,232,223,0.30)",
                  marginTop: 1,
                }}
                numberOfLines={1}
              >
                {roleLine}
              </Text>
              <View style={{ marginTop: 10, flexDirection: "row", alignItems: "center", gap: 14 }}>
                <Pressable
                  onPress={() => navTo("/admin/account" as Href)}
                  style={[
                    Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                  ]}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.bodyMedium,
                      fontSize: 10.5,
                      color: "rgba(237,232,223,0.50)",
                    }}
                  >
                    Account & security
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => {
                    closeDrawer();
                    void signOutToHome(router);
                  }}
                  style={[
                    Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                  ]}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.bodyMedium,
                      fontSize: 10.5,
                      color: "rgba(237,232,223,0.30)",
                    }}
                  >
                    Sign out
                  </Text>
                </Pressable>
              </View>
            </View>
    </>
  );

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: BLACK }} edges={["top", "left", "right"]}>
      <View style={{ flex: 1, flexDirection: "row" }}>
        {showSidebar ? (
          <View
            style={{
              width: SIDEBAR_W,
              backgroundColor: FOREST,
              borderRightWidth: STROKES.hairline,
              borderRightColor: "rgba(181,135,58,0.12)",
              flexDirection: "column",
            }}
          >
            {sidebarBody}
          </View>
        ) : null}

        {/* Right column: topbar + main */}
        <View style={{ flex: 1, minWidth: 0, backgroundColor: BLACK }}>
          <View
            style={[
              {
                height: TOPBAR_H,
                paddingHorizontal: isMobile ? 12 : 24,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                borderBottomWidth: STROKES.hairline,
                borderBottomColor: BORDER,
                backgroundColor: "rgba(14,14,14,0.97)",
              },
              Platform.OS === "web"
                ? ({ backdropFilter: "blur(16px)" } as unknown as ViewStyle)
                : null,
            ]}
          >
            <View style={{ flexDirection: "row", alignItems: "center", flex: 1, minWidth: 0 }}>
              {isMobile ? <HamburgerButton onPress={openDrawer} color={LINEN} /> : null}
              <Text
                style={{
                  fontFamily: FONTS.displayMedium,
                  fontSize: 17,
                  color: W,
                  flexShrink: 1,
                }}
                numberOfLines={1}
              >
                {topbarTitle}
              </Text>
            </View>

            {/* Right cluster: global search (full pill on desktop, compact
                icon on mobile), a reserved quick-create, then the avatar. */}
            <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
              <SearchTrigger compact={isMobile} onPress={() => setSearchOpen(true)} />
              {isMobile ? null : <ReservedQuickCreate />}
              <Pressable
                ref={avatarRef}
                onPress={openAccountMenu}
                accessibilityRole="button"
                accessibilityLabel="Account menu"
                style={[
                  {
                    width: 28,
                    height: 28,
                    borderRadius: 14,
                    backgroundColor: FOREST_MID,
                    borderWidth: STROKES.hairline,
                    borderColor: accountMenuOpen ? AMBER : "rgba(181,135,58,0.30)",
                    alignItems: "center",
                    justifyContent: "center",
                  } as ViewStyle,
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text
                  style={{
                    fontFamily: FONTS.displaySemiBold,
                    fontSize: 12.5,
                    color: AMBER,
                  }}
                >
                  {initials}
                </Text>
              </Pressable>
            </View>
          </View>

          <View style={{ flex: 1, minHeight: 0, backgroundColor: BLACK }}>
            {children}
          </View>
        </View>
      </View>

      {/* Mobile drawer — same sidebar contents, mounted only when narrow */}
      <ShellMobileDrawer
        open={drawerOpen}
        onRequestClose={closeDrawer}
        backgroundColor={FOREST}
      >
        {sidebarBody}
      </ShellMobileDrawer>

      {/* Account menu — small dropdown anchored to the topbar avatar */}
      <RNModal
        visible={accountMenuOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setAccountMenuOpen(false)}
      >
        <Pressable
          onPress={() => setAccountMenuOpen(false)}
          style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
        />
        <View
          style={{
            position: "absolute",
            top: menuAnchor.top,
            right: menuAnchor.right,
            width: 220,
            backgroundColor: BLACK,
            borderWidth: STROKES.hairline,
            borderColor: BORDER,
            borderRadius: RADII.sm,
            overflow: "hidden",
          }}
        >
          <View
            style={{
              paddingHorizontal: 14,
              paddingVertical: 10,
              borderBottomWidth: STROKES.hairline,
              borderBottomColor: BORDER,
            }}
          >
            <Text
              style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: LINEN }}
              numberOfLines={1}
            >
              {displayName}
            </Text>
            <Text
              style={{
                marginTop: 1,
                fontFamily: FONTS.body,
                fontSize: 10.5,
                color: "rgba(237,232,223,0.45)",
              }}
              numberOfLines={1}
            >
              {roleLine}
            </Text>
          </View>
          <DropdownItem
            label="Account & security"
            onPress={() => {
              setAccountMenuOpen(false);
              navTo("/admin/account" as Href);
            }}
          />
          <DropdownItem
            label="Sign out"
            onPress={() => {
              setAccountMenuOpen(false);
              void signOutToHome(router);
            }}
          />
        </View>
      </RNModal>

      {/* Global search palette — Cmd/Ctrl+K or "/" from anywhere in admin. */}
      <AdminGlobalSearchOverlay visible={searchOpen} onClose={() => setSearchOpen(false)} />
    </SafeAreaView>
  );
}

function DropdownItem({ label, onPress }: { label: string; onPress: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      {...(Platform.OS === "web"
        ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const)
        : {})}
      style={[
        {
          paddingHorizontal: 14,
          paddingVertical: 11,
          backgroundColor: hover ? THEME.accentTintSoft : "transparent",
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: hover ? AMBER : LINEN }}>
        {label}
      </Text>
    </Pressable>
  );
}

/* ──────────────────────  Top-bar reserved slots  ─────────────────────── */

// Top-bar search affordance. Opens the global search palette; the palette also
// answers to Cmd/Ctrl+K and "/" from anywhere (see the shell-level handler).
// Full pill on desktop, a compact icon button where space is tight (mobile).
function SearchTrigger({ compact, onPress }: { compact?: boolean; onPress: () => void }) {
  const [hover, setHover] = useState(false);
  const isWeb = Platform.OS === "web";
  const hoverProps = isWeb
    ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const)
    : {};

  if (compact) {
    return (
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel="Search"
        {...hoverProps}
        style={[
          {
            width: 28,
            height: 28,
            borderRadius: 14,
            alignItems: "center",
            justifyContent: "center",
            borderWidth: STROKES.hairline,
            borderColor: hover && isWeb ? THEME.accentBorderSoft : BORDER,
            backgroundColor: THEME.neutralHoverBgOnDark,
          } as ViewStyle,
          isWeb ? ({ cursor: "pointer" } as const) : null,
        ]}
      >
        <IconSearch color={hover && isWeb ? AMBER : "rgba(237,232,223,0.55)"} size={14} />
      </Pressable>
    );
  }

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel="Search"
      {...hoverProps}
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 7,
          width: 220,
          height: 30,
          paddingHorizontal: 10,
          borderRadius: RADII.full,
          borderWidth: STROKES.hairline,
          borderColor: hover && isWeb ? THEME.accentBorderSoft : BORDER,
          backgroundColor: THEME.neutralHoverBgOnDark,
        } as ViewStyle,
        isWeb ? ({ cursor: "pointer", transition: "border-color 120ms" } as object) : null,
      ]}
    >
      <IconSearch color={hover && isWeb ? AMBER : "rgba(237,232,223,0.50)"} size={13} />
      <Text
        style={{
          flex: 1,
          fontFamily: FONTS.body,
          fontSize: 12,
          color: "rgba(237,232,223,0.48)",
        }}
        numberOfLines={1}
      >
        Search
      </Text>
      <View
        style={{
          paddingHorizontal: 5,
          paddingVertical: 1,
          borderRadius: RADII.xs,
          borderWidth: STROKES.hairline,
          borderColor: BORDER,
        }}
      >
        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 9.5, color: "rgba(237,232,223,0.45)" }}>
          ⌘K
        </Text>
      </View>
    </Pressable>
  );
}

// Placeholder quick-create. Reserves the slot next to the avatar for a future
// global "+ new" menu. Inert + dimmed, same rationale as the search slot.
function ReservedQuickCreate() {
  return (
    <View
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      {...(Platform.OS === "web" ? ({ title: "Quick create — coming soon" } as object) : {})}
      style={{
        width: 28,
        height: 28,
        borderRadius: 14,
        alignItems: "center",
        justifyContent: "center",
        borderWidth: STROKES.hairline,
        borderColor: BORDER,
        backgroundColor: THEME.neutralHoverBgOnDark,
        opacity: 0.6,
      }}
    >
      <IconPlus color="rgba(237,232,223,0.45)" />
    </View>
  );
}

/* ─────────────────────────  Sidebar primitive  ───────────────────────── */

function SidebarNavItem({
  label,
  icon,
  active,
  onPress,
  badge,
  isQuickCreate,
  muted,
}: {
  label: string;
  icon: ReactNode;
  active: boolean;
  onPress: () => void;
  badge?: number;
  isQuickCreate?: boolean;
  muted?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const isWebHover = Platform.OS === "web" && hover && !active;
  const showBadge = (badge ?? 0) > 0;
  return (
    <Pressable
      onPress={onPress}
      {...(Platform.OS === "web"
        ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const)
        : {})}
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 9,
          paddingHorizontal: 18,
          paddingVertical: 7,
          borderLeftWidth: STROKES.strong,
          borderLeftColor: active ? AMBER : "transparent",
          backgroundColor: active
            ? THEME.accentTintSoft
            : isWebHover
              ? THEME.neutralHoverBgOnDark
              : "transparent",
          marginBottom: 1,
        } as ViewStyle,
        Platform.OS === "web"
          ? ({
              transition: "background-color 120ms, color 120ms",
            } as object)
          : null,
      ]}
    >
      <View style={{ opacity: active ? 1 : isQuickCreate ? 0.85 : 0.5 }}>{icon}</View>
      <Text
        style={{
          flex: 1,
          fontFamily: isQuickCreate
            ? FONTS.bodyBold
            : active
              ? FONTS.bodySemiBold
              : FONTS.bodyMedium,
          fontSize: 12.5,
          letterSpacing: isQuickCreate ? 0.4 : 0,
          color: isQuickCreate
            ? AMBER
            : active
              ? LINEN
              : muted
                ? "rgba(237,232,223,0.34)"
                : "rgba(237,232,223,0.42)",
        }}
        numberOfLines={1}
      >
        {label}
      </Text>
      {showBadge ? (
        <View
          style={{
            paddingHorizontal: 6,
            paddingVertical: 1,
            borderRadius: RADII.full,
            backgroundColor: AMBER,
          }}
        >
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9, color: FOREST }}>
            {badge! > 99 ? "99+" : String(badge)}
          </Text>
        </View>
      ) : null}
    </Pressable>
  );
}

